/**
 * cargo-ai/navigate 意图 → 基座路由映射。
 * 映射表归基座所有（协议：AI 侧永不发送路由内部形态，漂移只在本文件修）。
 *
 * v1 仅海运出口 SE 工作区：
 * - look + job_no   → 工作区多开标签（?openNew=true&key=<jobNo>，组件按 key 自查数据）
 * - look + job_uuid → toDetailByUuid 口径（bookingNew.vue activated 分支，uuid 精确主键定位）
 * - copy            → 取源单行后调 SeaBooking 实例的 copyHandle(row)，
 *                     复制选项与确认全部走 cargo 自己的弹框流程（协议：AI 只导航不写数据）。
 *                     不走 openNew+params.action='copy'：onShow('copy') 期待的是已落库的新票，
 *                     源单透传只会把源单当复制结果显示。
 */
import { matchView, refreshView } from '@/store/modules/tagsView'
import { findSeaExportJobByNo, findSeaExportJobByUuid } from '@/api/seaExport/seaExportJob'

const SE_PERMISSION = ['module_fmBookingNew']
const SE_BOOKING_COMPONENT = 'SeaBooking'

function pushSafely(vm, location) {
  const result = vm.$router.push(location)
  if (result && typeof result.catch === 'function') {
    result.catch(() => {})
  }
}

// 同路由重复导航时 router 不会动，参照 AppMain.toLink 手动刷新视图让 params 生效
function pushWithRefresh(vm, location) {
  const oldRoute = vm.$route
  pushSafely(vm, location)
  setTimeout(() => {
    if (vm.$route === oldRoute && location.params && Object.keys(location.params).length > 0) {
      const view = vm.$store.state.tagsView.visitedViews.find(v => matchView(v, vm.$route))
      if (view) {
        Object.assign(view.params, location.params)
        refreshView(view)
      }
    }
  }, 100)
}

/**
 * 在组件树中按 name 查找组件实例
 */
export function findComponentByName(rootInstance, name) {
  if (!rootInstance) return null
  if (rootInstance.$options && rootInstance.$options.name === name) {
    return rootInstance
  }
  const children = rootInstance.$children || []
  for (const child of children) {
    const found = findComponentByName(child, name)
    if (found) return found
  }
  return null
}

function findCurrentSeaBooking(vm) {
  const instances = vm.$route.matched
    .map(record => record.instances && record.instances.default)
    .filter(Boolean)
  for (const instance of instances) {
    const found = findComponentByName(instance, SE_BOOKING_COMPONENT)
    if (found) return found
  }
  return null
}

// keep-alive 命中时导航完成实例即在树上；新挂载则等 nextTick 轮询
function ensureSeaBookingInstance(vm, attempts = 10) {
  return new Promise((resolve, reject) => {
    let remaining = attempts
    const tryFind = () => {
      const found = findCurrentSeaBooking(vm)
      if (found) {
        resolve(found)
        return
      }
      remaining -= 1
      if (remaining <= 0) {
        reject(new Error('SeaBooking instance not ready'))
        return
      }
      vm.$nextTick(() => setTimeout(tryFind, 50))
    }
    vm.$nextTick(tryFind)
  })
}

function lookSeaBooking(vm, intent) {
  if (intent.jobUuid) {
    pushWithRefresh(vm, { name: SE_BOOKING_COMPONENT, params: { jobUuid: intent.jobUuid }})
    return
  }
  pushSafely(vm, { name: SE_BOOKING_COMPONENT, query: { openNew: 'true', key: intent.jobNo }})
}

async function fetchSeJobRow(vm, intent) {
  const branchId = vm.$store.getters.currentOfficeId
  let res
  if (intent.jobUuid) {
    res = await findSeaExportJobByUuid({ jobUuid: intent.jobUuid, branchId, pageNum: 1 })
    const list = (res.data && res.data.list) || []
    return list.find(p => p.jobUuid === intent.jobUuid) || list[0] || null
  }
  res = await findSeaExportJobByNo({ jobNo: intent.jobNo, branchId, pageNum: 1 })
  const list = (res.data && res.data.list) || []
  return list.find(p => p.jobNo === intent.jobNo) || list[0] || null
}

async function openCopyScenario(vm, intent) {
  let instance = findCurrentSeaBooking(vm)
  if (!instance) {
    pushWithRefresh(vm, { name: SE_BOOKING_COMPONENT })
    try {
      instance = await ensureSeaBookingInstance(vm)
    } catch (err) {
      console.warn('[CargoAi] 工作区实例未就绪，放弃复制导航:', err)
      return
    }
  }
  let row = null
  try {
    row = await fetchSeJobRow(vm, intent)
  } catch (err) {
    console.warn('[CargoAi] 查询订单失败:', err)
  }
  if (!row) {
    vm.$message.error(vm.$t('Message.NoRecordMeetTheConditions'))
    return
  }
  instance.copyHandle(row)
}

/**
 * 导航意图分发入口（CargoAiHost 的 message 监听调用）
 * @param {Vue} vm - 宿主组件实例（提供 $router/$store/$message/$t/$checkPermission）
 * @param {Object} intent - parseNavigateMessage 的归一化结果
 */
export async function handleNavigate(vm, intent) {
  if (intent.page !== 'job_workspace' || intent.module !== 'SE') {
    console.warn('[CargoAi] 未映射的导航目标:', intent.page, intent.module)
    return
  }
  if (!vm.$checkPermission(SE_PERMISSION)) {
    vm.$message.error(vm.$t('Message.YouToOpenTheModule', [vm.$t('CargoMsg.Cargo_SeaBooking')]))
    return
  }
  if (intent.action === 'copy') {
    await openCopyScenario(vm, intent)
    return
  }
  lookSeaBooking(vm, intent)
}
